if (!customElements.get('gavyamart-certifications')) {
  class GavyamartCertifications extends HTMLElement {
    constructor() {
      super();
      this.position = 0;
      this.lastTime = 0;
      this.groupWidth = 0;
      this.dragging = false;
      this.dragMoved = false;
      this.pausedByUser = false;
      this.pausedByHover = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.speed = Number(this.dataset.speed) || 34;
      this.pauseOnHover = this.dataset.pauseHover === 'true';
      this.tick = this.tick.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-cert-viewport]');
      this.track = this.querySelector('[data-cert-track]');
      this.group = this.querySelector('[data-cert-group]');
      this.control = this.querySelector('[data-cert-control]');
      this.controlLabel = this.querySelector('[data-cert-control-label]');
      this.status = this.querySelector('[data-cert-status]');
      if (!this.viewport || !this.track || !this.group) return;

      this.measure = () => {
        this.groupWidth = this.group.getBoundingClientRect().width;
        this.ensureCopies();
      };
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.group);
      this.measure();

      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event));
      this.viewport.addEventListener('click', (event) => {
        if (this.suppressClick) event.preventDefault();
      });
      this.control?.addEventListener('click', () => this.setPaused(!this.pausedByUser));
      if (this.pauseOnHover) {
        this.viewport.addEventListener('mouseenter', () => { this.pausedByHover = true; });
        this.viewport.addEventListener('mouseleave', () => {
          this.pausedByHover = false;
          this.lastTime = performance.now();
        });
      }
      this.visibilityHandler = () => { this.lastTime = performance.now(); };
      document.addEventListener('visibilitychange', this.visibilityHandler);

      if (this.reduceMotion) this.setPaused(true, 'Movement paused for reduced-motion preference.');
      this.animationFrame = requestAnimationFrame(this.tick);
    }

    ensureCopies() {
      if (!this.groupWidth || !this.viewport) return;
      const requiredGroups = Math.max(2, Math.ceil(this.viewport.clientWidth / this.groupWidth) + 2);
      let groups = Array.from(this.track.querySelectorAll('.cert-carousel__group'));
      const cloneSource = groups[1] || this.group;

      while (groups.length < requiredGroups) {
        const clone = cloneSource.cloneNode(true);
        clone.removeAttribute('data-cert-group');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach((item) => item.setAttribute('tabindex', '-1'));
        this.track.appendChild(clone);
        groups.push(clone);
      }
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.animationFrame);
      this.resizeObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }

    normalizePosition() {
      if (!this.groupWidth) return;
      this.position = ((this.position % this.groupWidth) + this.groupWidth) % this.groupWidth;
    }

    render() {
      this.normalizePosition();
      this.track.style.transform = `translate3d(${-this.position}px, 0, 0)`;
    }

    tick(time) {
      const delta = this.lastTime ? Math.min((time - this.lastTime) / 1000, .05) : 0;
      this.lastTime = time;
      if (!this.pausedByUser && !this.pausedByHover && !this.dragging && !document.hidden && !this.reduceMotion) {
        this.position += this.speed * delta;
        this.render();
      }
      this.animationFrame = requestAnimationFrame(this.tick);
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.dragging = true;
      this.dragMoved = false;
      this.startX = event.clientX;
      this.lastX = event.clientX;
      this.viewport.classList.add('is-dragging');
      this.viewport.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      const deltaX = event.clientX - this.lastX;
      if (Math.abs(event.clientX - this.startX) > 6) this.dragMoved = true;
      this.position -= deltaX;
      this.lastX = event.clientX;
      this.render();
    }

    onPointerUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      this.viewport.classList.remove('is-dragging');
      if (this.viewport.hasPointerCapture?.(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
      if (this.dragMoved) {
        this.suppressClick = true;
        window.setTimeout(() => { this.suppressClick = false; }, 0);
      }
      this.lastTime = performance.now();
    }

    setPaused(paused, message) {
      this.pausedByUser = paused;
      this.classList.toggle('is-paused', paused);
      if (this.control) this.control.setAttribute('aria-pressed', String(paused));
      if (this.controlLabel) this.controlLabel.textContent = paused ? 'Resume movement' : 'Pause movement';
      if (this.status) this.status.textContent = message || (paused ? 'Carousel paused.' : 'Carousel resumed.');
      if (!paused) {
        this.reduceMotion = false;
        this.lastTime = performance.now();
      }
    }
  }

  customElements.define('gavyamart-certifications', GavyamartCertifications);
}
